import { api } from "../lib/api";
import { session } from "../stores/session";

export type VatPeriodStatus = "open" | "locked";
export type FrsTurnoverMethod = "cash" | "invoice";
export type VatScheme = "none" | "flat_rate" | "standard_accrual" | "cash_accounting";

export interface VatPeriod {
  id: string;
  company_id: string;
  start_date: string;
  end_date: string;
  status: VatPeriodStatus;
  locked_at: string | null;
  scheme_snapshot: VatScheme | null;
  frs_rate_snapshot: string | null;
  frs_turnover_method_snapshot: FrsTurnoverMethod | null;
  box1_vat_due: string | null;
  box6_turnover: string | null;
  created_at: string;
  updated_at: string;
}

export interface VatReportLine {
  kind: "invoice" | "payment";
  date_counted: string;
  invoice_id: string;
  invoice_number: string | null;
  client_name: string;
  currency: string;
  gross_amount: string;
  gross_amount_gbp: string;
  fx_rate_to_gbp: string;
  /** Why this line counts (or doesn't): zero-rated and exempt supplies belong in
   * flat-rate turnover, outside-scope ones don't. */
  vat_treatment: VatTreatment;
}

export type VatTreatment =
  | "standard"
  | "reduced"
  | "zero_rated"
  | "exempt"
  | "outside_scope";

export interface VatReportFlags {
  unpaid_invoices_excluded: string[];
  non_gbp_invoice_numbers: string[];
}

export interface VatReport {
  period: VatPeriod;
  scheme: VatScheme;
  turnover_method: FrsTurnoverMethod;
  frs_rate_applied: string | null;
  first_year_discount_applied: boolean;
  lines: VatReportLine[];
  /** Supplies outside the scope of UK VAT, listed so the turnover figure is auditable. */
  excluded_lines: VatReportLine[];
  excluded_turnover_gbp: string;
  /** Set when most of the period's invoicing is outside scope — a question, not advice. */
  scheme_suitability_note: string | null;
  gross_turnover_gbp: string;
  box1_vat_due: string;
  box6_turnover: string;
  flags: VatReportFlags;
}

export function listVatPeriods(): Promise<VatPeriod[]> {
  return api.get<VatPeriod[]>("/api/vat/periods");
}

export interface SkippedClosedYear {
  start_date: string;
  end_date: string;
}

export interface VatPeriodBackfill {
  created: VatPeriod[];
  already_present: number;
  /** Quarters deliberately not created because their accounting year is closed. */
  skipped_closed_years: SkippedClosedYear[];
}

export function backfillVatPeriods(): Promise<VatPeriodBackfill> {
  return api.post<VatPeriodBackfill>("/api/vat/periods/backfill");
}

export function getVatReport(periodId: string): Promise<VatReport> {
  return api.get<VatReport>(`/api/vat/periods/${periodId}/report`);
}

export function lockVatPeriod(periodId: string): Promise<VatPeriod> {
  return api.post<VatPeriod>(`/api/vat/periods/${periodId}/lock`);
}

export function unlockVatPeriod(periodId: string): Promise<VatPeriod> {
  return api.post<VatPeriod>(`/api/vat/periods/${periodId}/unlock`);
}

/** Not fetched via the JSON api client — plain browser navigation/download links,
 * same "browser navigates directly" pattern as invoicePdfUrl (services/invoice.ts). */
function reportExportUrl(periodId: string, format: "csv" | "pdf"): string {
  const params = new URLSearchParams({
    format,
    company_id: session.companyId.value ?? "",
  });
  return `/api/vat/periods/${periodId}/report?${params.toString()}`;
}

export function vatReportCsvUrl(periodId: string): string {
  return reportExportUrl(periodId, "csv");
}

export function vatReportPdfUrl(periodId: string): string {
  return reportExportUrl(periodId, "pdf");
}

/** The whole period as one zip for the accountant. Its own route rather than another
 * `format=`, because the package contains the report rather than rendering it. */
export function vatReportPackageUrl(periodId: string): string {
  const params = new URLSearchParams({ company_id: session.companyId.value ?? "" });
  return `/api/vat/periods/${periodId}/package?${params.toString()}`;
}

/** Treatments that charge no VAT, so the rate must be 0. Mirrors the backend's
 * ZERO_VAT_TREATMENTS and the ck_invoice_vat_treatment_rate constraint. */
export const ZERO_VAT_TREATMENTS: ReadonlySet<VatTreatment> = new Set<VatTreatment>([
  "zero_rated",
  "exempt",
  "outside_scope",
]);

export const VAT_TREATMENT_LABELS: Record<VatTreatment, string> = {
  standard: "Standard",
  reduced: "Reduced",
  zero_rated: "Zero-rated",
  exempt: "Exempt",
  outside_scope: "Outside scope of UK VAT",
};
